import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { useValidateRequestProjectSubmissionLink } from '@/features/request-project/hooks/use-validate-request-project-submission-link.query';
import { Loader } from '@/components/loader/loader';
import { InvalidLinkPage, PageNotFound } from '@/routes/page-not-found';
import { isInvalidLinkError } from '@/helpers/is-invalid-link-error';
import { useAccountSwitchOnLinkValidation } from '@/features/account/hooks/use-account-switch-on-link-validation';
import { useAuthorization } from '@/auth/hooks/use-authorization';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { useCurrentUser } from '@/user/use-current-user.query';
import { QueryKey } from '@/config/query-client';
import { Flexbox } from '@/ui/flexbox/flexbox';

const REDIRECT_PATH = '/projects?activeTab=pendingProjects';

export const RequestProjectSubmissionWithLinkWrapperPage = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const { t } = useTranslation('default');
  const { isUserAuthorized } = useAuthorization();
  const navigateTo = useNavigateTo();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const {
    data: validationData,
    isLoading,
    error,
  } = useValidateRequestProjectSubmissionLink(linkId || '');

  const [shouldRedirectAfterSwitch, setShouldRedirectAfterSwitch] =
    useState(false);
  const previousAccountNameRef = useRef<string>('');

  useEffect(() => {
    setShouldRedirectAfterSwitch(false);

    if (linkId) {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.RequestProjectSubmissionLink, 'validate', linkId],
      });
    }

    return () => {
      setShouldRedirectAfterSwitch(false);
    };
  }, [linkId, queryClient]);

  const onSwitchComplete = useCallback((previousAccountName: string) => {
    previousAccountNameRef.current = previousAccountName;
    setShouldRedirectAfterSwitch(true);
  }, []);

  const { handleAccountSwitch, isSwitching, shouldSwitch } =
    useAccountSwitchOnLinkValidation({
      validationData,
      onSwitchComplete,
    });

  const getRedirectUrlWithNotification = useCallback(() => {
    if (!validationData) {
      return REDIRECT_PATH;
    }

    const url = new URL(REDIRECT_PATH, window.location.origin);
    url.searchParams.set('accountSwitched', 'true');
    url.searchParams.set('accountName', validationData.accountName || '');
    url.searchParams.set('previousAccountName', previousAccountNameRef.current);
    url.searchParams.set('entityType', 'request project');

    return url.pathname + url.search;
  }, [validationData]);

  useEffect(() => {
    if (shouldSwitch && !shouldRedirectAfterSwitch) {
      handleAccountSwitch();
    }
  }, [shouldSwitch, handleAccountSwitch, shouldRedirectAfterSwitch]);

  useEffect(() => {
    if (shouldRedirectAfterSwitch && !isSwitching) {
      const redirectUrl = getRedirectUrlWithNotification();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    }
  }, [shouldRedirectAfterSwitch, isSwitching, getRedirectUrlWithNotification]);

  const isLoadingState = isLoading || isSwitching;
  const isValidRedirect =
    validationData?.isValid &&
    validationData?.redirectPath &&
    validationData?.accountId &&
    currentUser.data &&
    currentUser.data.account.id === validationData.accountId;
  const shouldShowNotFound =
    !isLoading &&
    !shouldRedirectAfterSwitch &&
    (error || !validationData?.isValid || !validationData?.redirectPath);

  const loadingComponent = useMemo(
    () => (
      <WithHtmlSavvyTitle title={t`loading` || 'Loading...'}>
        <Flexbox justify="center" alignItems="center" height={`100vh`}>
          <Loader />
        </Flexbox>
      </WithHtmlSavvyTitle>
    ),
    [t],
  );

  if (isLoadingState) {
    return loadingComponent;
  }

  if (shouldShowNotFound) {
    const isInvalidLink = !error || isInvalidLinkError(error);
    return isInvalidLink ? <InvalidLinkPage /> : <PageNotFound />;
  }

  if ((isLoading || !validationData) && !shouldRedirectAfterSwitch) {
    return loadingComponent;
  }

  if (!isUserAuthorized) {
    const currentPath = `${location.pathname}${location.search}`;
    const redirectUrl = `redirectTo=${encodeURIComponent(currentPath)}`;
    navigateTo.signIn(undefined, redirectUrl);
    return loadingComponent;
  }

  if (shouldRedirectAfterSwitch && !isSwitching) {
    return loadingComponent;
  }

  if (
    !shouldSwitch &&
    !isSwitching &&
    !shouldRedirectAfterSwitch &&
    isValidRedirect &&
    validationData.redirectPath
  ) {
    return <Navigate to={validationData.redirectPath} replace />;
  }

  return loadingComponent;
};
